/**
 * Per embed-conversation-shortcuts spec — corner-anchored floating
 * action button with a three-step expansion:
 *   1. Collapsed   — only the FAB.
 *   2. Category strip — horizontal row of icon-only category chips.
 *   3. Item list   — vertical stack of text-only item chips for the
 *      selected category.
 *
 * Tapping an item calls `onDispatch(item.description)` and immediately
 * collapses. Auto-collapses on: Escape, click-outside, 6 s idle, or
 * `isMidReply` going true (FAB fades out while the AI replies).
 *
 * The same component is rendered by:
 *   - The public embed page (`EmbedViewer`) — real shortcuts dispatch.
 *   - The admin Shortcuts tab's live preview — pass `onDispatch={() => {}}`
 *     and a static `isMidReply={false}` to render against the draft.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import type { Shortcuts, ShortcutCategory, ShortcutItem } from '@nxorbit/shared-schema';
import { ShortcutIcon } from './ShortcutIcon';

interface Props {
  shortcuts: Shortcuts;
  onDispatch: (description: string) => void;
  isMidReply: boolean;
}

type Mode = 'collapsed' | 'category-strip' | 'item-list';

const AUTO_COLLAPSE_MS = 6000;

const POSITION_CLASSES: Record<Shortcuts['position'], string> = {
  'top-left': 'top-4 left-4',
  'top-right': 'top-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'bottom-right': 'bottom-4 right-4',
};

export function ShortcutsFab({ shortcuts, onDispatch, isMidReply }: Props): ReactElement | null {
  const [mode, setMode] = useState<Mode>('collapsed');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const idleTimerRef = useRef<number | null>(null);

  const visible = shortcuts.enabled && shortcuts.categories.length > 0;
  const muted = isMidReply;

  // Reset idle timer on any panel interaction; collapse when it fires.
  const armIdle = useCallback((): void => {
    if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current);
    if (mode === 'collapsed') return;
    idleTimerRef.current = window.setTimeout(() => {
      setMode('collapsed');
      setSelectedCategoryId(null);
    }, AUTO_COLLAPSE_MS);
  }, [mode]);

  useEffect(() => {
    armIdle();
    return () => {
      if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current);
    };
  }, [mode, armIdle]);

  // Click-outside collapse.
  useEffect(() => {
    if (mode === 'collapsed') return;
    function onPointerDown(e: PointerEvent): void {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target as Node)) return;
      setMode('collapsed');
      setSelectedCategoryId(null);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [mode]);

  // Escape collapse.
  useEffect(() => {
    if (mode === 'collapsed') return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        setMode('collapsed');
        setSelectedCategoryId(null);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mode]);

  // Auto-collapse the moment AI starts replying.
  useEffect(() => {
    if (isMidReply && mode !== 'collapsed') {
      setMode('collapsed');
      setSelectedCategoryId(null);
    }
  }, [isMidReply, mode]);

  if (!visible) return null;

  function handleFabTap(): void {
    if (muted) return;
    setMode((m) => (m === 'collapsed' ? 'category-strip' : 'collapsed'));
    setSelectedCategoryId(null);
  }

  function handleCategoryTap(cat: ShortcutCategory): void {
    if (muted) return;
    if (selectedCategoryId === cat.id) {
      // Same category tapped → back to strip.
      setMode('category-strip');
      setSelectedCategoryId(null);
      return;
    }
    setSelectedCategoryId(cat.id);
    setMode('item-list');
  }

  function handleItemTap(item: ShortcutItem): void {
    if (muted) return;
    onDispatch(item.description);
    setMode('collapsed');
    setSelectedCategoryId(null);
  }

  const selectedCategory = shortcuts.categories.find((c) => c.id === selectedCategoryId) ?? null;

  return (
    <div
      ref={panelRef}
      className={`pointer-events-auto absolute z-30 flex flex-col gap-2 transition-opacity duration-200 ${
        POSITION_CLASSES[shortcuts.position]
      } ${muted ? 'pointer-events-none opacity-0' : 'opacity-100'} ${
        shortcuts.position.startsWith('bottom') ? 'items-end flex-col-reverse' : 'items-start'
      } ${shortcuts.position.endsWith('right') ? 'items-end' : 'items-start'}`}
      onPointerMove={armIdle}
    >
      <button
        type="button"
        onClick={handleFabTap}
        aria-label={mode === 'collapsed' ? '開啟快捷鍵' : '關閉快捷鍵'}
        aria-expanded={mode !== 'collapsed'}
        className={`group flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-slate-900/80 text-slate-100 shadow-xl backdrop-blur-md transition active:scale-95 hover:border-white/20 hover:bg-slate-800/85 ${
          mode !== 'collapsed' ? 'ring-2 ring-amber-400/40' : ''
        }`}
      >
        <span className={`text-[15px] leading-none transition-transform duration-200 ${mode !== 'collapsed' ? 'rotate-90' : 'group-hover:scale-110'}`}>
          {mode === 'collapsed' ? '✦' : '×'}
        </span>
      </button>

      {mode === 'category-strip' && (
        <div
          role="menu"
          aria-label="快捷分類"
          className={`flex gap-1 rounded-2xl border border-white/10 bg-slate-900/85 p-1.5 shadow-2xl backdrop-blur-md ${
            shortcuts.position.endsWith('right') ? 'flex-row-reverse' : 'flex-row'
          }`}
        >
          {shortcuts.categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleCategoryTap(cat)}
              aria-label={cat.label}
              title={cat.label}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-300 transition active:scale-95 hover:bg-white/10 hover:text-white"
            >
              <ShortcutIcon icon={cat.icon} className="h-5 w-5" ariaLabel={cat.label} />
            </button>
          ))}
        </div>
      )}

      {mode === 'item-list' && selectedCategory && (
        <div
          role="menu"
          aria-label={`${selectedCategory.label} 的快捷項目`}
          className={`flex max-h-[60vh] flex-col gap-1 overflow-y-auto rounded-2xl border border-white/10 bg-slate-900/85 p-1.5 shadow-2xl backdrop-blur-md ${
            shortcuts.position.endsWith('right') ? 'items-end' : 'items-start'
          }`}
        >
          {/* Category header strip */}
          <div className="flex w-full items-center gap-2 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">
            <ShortcutIcon icon={selectedCategory.icon} className="h-3 w-3" ariaLabel="" />
            <span className="truncate">{selectedCategory.label}</span>
          </div>
          {selectedCategory.items.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">尚無項目</p>
          ) : (
            selectedCategory.items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleItemTap(item)}
                title={item.description}
                className="w-full max-w-[260px] rounded-xl border border-white/5 bg-white/[0.03] px-4 py-2 text-sm font-medium text-slate-100 transition active:scale-[0.98] hover:border-white/15 hover:bg-white/10"
              >
                {item.title}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default ShortcutsFab;
